import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Grid, InputAdornment, TextField,
} from '@mui/material';
import {
  ConstructionOption, ConstructionSearchOption, ObraProperties, ProcesoConstructivo,
} from '../../../../../../types/ObraDetails';
import { ValidationError } from '../../../../../../types/ValidationError';
import { SaveContext } from '../../../../../../context/SaveProvider';
import DeleteButton from '../../../../components/deleteButton/deleteButton';
import ObrasAPI from '../../../../../../services/APIObras';
import { TechnicalAspectsContext } from '../../../technicalAspects';
import ConstructionSelect from './constructionSelect';
import TitleValue from '../../../../components/titleValue/titleValue';
import searchStyles from '../../../../components/searchAutocomplete/searchAutocomplete.module.scss';
import searchIcon from '../../../../../../assets/search.svg';
import { ConstructionUnitOption, losesMeasurements, replacementUnits, sameSelection } from './constructionUnits';

interface DataProcessProps {
  obra: ObraProperties;
  process: ProcesoConstructivo;
  index: number;
  handleDelete: (process: ProcesoConstructivo, index: number) => void;
  error?: ValidationError[];
  chapters: ConstructionOption[];
}

const canonicalCode = (code: string | number | undefined, length: number) =>
  code === undefined || code === '' ? '' : String(code).padStart(length, '0');

const DataProcess: FC<DataProcessProps> = ({ obra, process, index, handleDelete, error, chapters }) => {
  const [t, i18n] = useTranslation();
  const text = (key: string) => t(`details.technical.buildingProcess.${key}`) || '';
  const saveContext = useContext(SaveContext);
  const { setBuildingProcessUnits } = useContext(TechnicalAspectsContext);
  const api = ObrasAPI.getInstance();
  const [row, setRow] = useState(process);
  const [groups, setGroups] = useState<ConstructionOption[]>([]);
  const [processes, setProcesses] = useState<ConstructionOption[]>([]);
  const [options, setOptions] = useState<ConstructionSearchOption[]>([]);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [duplicateSelection, setDuplicateSelection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<ProcesoConstructivo | null>(null);
  const [pending, setPending] = useState<ProcesoConstructivo | null>(null);
  const [emptyUnits, setEmptyUnits] = useState(false);
  const [reload, setReload] = useState(0);
  const request = useRef(0);
  const mounted = useRef(false);
  const chapterCode = canonicalCode(row.capitulo?.codigo ?? row.capitulo?.id, 2);
  const groupCode = canonicalCode(row.codigo, 4);
  const selectedCode = canonicalCode(row.procesoConstructivo?.codigo, 5) || groupCode;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current += 1; };
  }, []);

  useEffect(() => { setInput(selectedCode); }, [selectedCode]);

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    setCatalogError(false);
    setGroups([]);
    setProcesses([]);
    Promise.all([
      chapterCode ? api.getGroupChapter(chapterCode) : Promise.resolve([]),
      groupCode ? api.getAutocompleteProcess(groupCode) : Promise.resolve([]),
    ]).then(([nextGroups, nextProcesses]) => {
      if (!active) return;
      setGroups(nextGroups);
      setProcesses(nextProcesses);
    }).catch(() => { if (active) setCatalogError(true); })
      .finally(() => { if (active) setCatalogLoading(false); });
    return () => { active = false; };
  }, [api, chapterCode, groupCode, reload, i18n.language]);

  useEffect(() => {
    let active = true;
    setOptions([]);
    setSearchError(false);
    setSearchLoading(false);
    if (!query.trim()) return;
    setSearchLoading(true);
    api.searchConstructionProcesses(query).then((results) => {
      if (active) setOptions(results);
    }).catch(() => { if (active) setSearchError(true); })
      .finally(() => { if (active) setSearchLoading(false); });
    return () => { active = false; };
  }, [api, query, reload, i18n.language]);

  const syncUnits = () => setBuildingProcessUnits(obra.aspectosTecnicos.procesosConstructivos
    .map((item, itemIndex) => ({ index: itemIndex, units: item.unidades })));

  const commit = (next: ProcesoConstructivo) => {
    Object.assign(process, next);
    setRow({ ...process });
    setInput(next.procesoConstructivo?.codigo || next.codigo);
    setPending(null);
    setFailed(null);
    setEmptyUnits(next.unidades.length === 0);
    syncUnits();
    saveContext.handleChangeSave(true);
  };

  const prepare = async (next: ProcesoConstructivo) => {
    const ticket = ++request.current;
    setPending(null);
    setFailed(null);
    setLoading(false);
    setDuplicateSelection(false);
    if (next.codigo && obra.aspectosTecnicos.procesosConstructivos.some((item) => item !== process
      && canonicalCode(item.codigo, 4) === canonicalCode(next.codigo, 4)
      && canonicalCode(item.procesoConstructivo?.codigo, 5) === canonicalCode(next.procesoConstructivo?.codigo, 5))) {
      setDuplicateSelection(true);
      setInput(selectedCode);
      return;
    }
    if (sameSelection({ ...process, capitulo: { ...process.capitulo, codigo: chapterCode, nombre: process.capitulo?.nombre || '' } }, next)) {
      setInput(selectedCode);
      return;
    }
    setLoading(true);
    try {
      const incoming: ConstructionUnitOption[] = next.procesoConstructivo
        ? await api.getUnitsByBuildingProcess(next.procesoConstructivo.codigo)
        : next.codigo ? await api.getUnitsByBuildingProcessGroup(next.codigo) : [];
      if (!mounted.current || ticket !== request.current) return;
      const excluded = new Set([
        ...obra.aspectosTecnicos.unidades,
        ...obra.aspectosTecnicos.procesosConstructivos.filter((item) => item !== process).flatMap((item) => item.unidades),
      ].map((unit) => unit.unidad?.codUnidad).filter((code): code is string => !!code));
      next.unidades = replacementUnits(process.unidades, incoming, excluded);
      if (losesMeasurements(process.unidades, next.unidades)) setPending(next);
      else commit(next);
    } catch (_error) {
      if (mounted.current && ticket === request.current) { setFailed(next); setInput(selectedCode); }
    } finally {
      if (mounted.current && ticket === request.current) setLoading(false);
    }
  };

  const selectChapter = (chapter: ConstructionOption) => void prepare({
    ...process, capitulo: chapter, codigo: '', nombre: '', procesoConstructivo: undefined,
  });
  const selectGroup = (group: ConstructionOption) => void prepare({
    ...process, capitulo: chapters.find((chapter) => chapter.codigo === chapterCode) || row.capitulo,
    codigo: group.codigo, nombre: group.nombre, procesoConstructivo: undefined,
  });
  const selectProcess = (value: ConstructionOption) => void prepare({
    ...process, capitulo: chapters.find((chapter) => chapter.codigo === chapterCode) || row.capitulo,
    procesoConstructivo: value,
  });

  const selectedChapter = row.capitulo ? { ...row.capitulo, codigo: chapterCode } : undefined;
  const selectedGroup = groupCode ? { codigo: groupCode, nombre: row.nombre } : undefined;
  const selectedProcess = row.procesoConstructivo
    ? { ...row.procesoConstructivo, codigo: canonicalCode(row.procesoConstructivo.codigo, 5) } : undefined;

  const noProcesses = !!groupCode && !catalogLoading && !catalogError && processes.length === 0;

  return <>
    <Grid item xs={1.5}>
      <div className={searchStyles.autocompleteContainer}>
        <TitleValue identifier={`construction-code-${index}`} title={text('code')} value={selectedCode}
          type="autocomplete" editable contrast={!searchFocused}>
          <Autocomplete<ConstructionSearchOption> id={`processAutoselect${index}Input`}
            options={options} value={null} inputValue={input} filterOptions={(values) => values}
            forcePopupIcon={false} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
            getOptionLabel={(option) => `${option.codigo} — ${option.nombre}`}
            isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
            loading={searchLoading} loadingText={text('loading')} noOptionsText={t('input.noResults')}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input' || reason === 'clear') { setInput(value); setQuery(value); }
            }}
            onChange={(_event, value) => {
              if (!value) { setInput(selectedCode); return; }
              void prepare({
                ...process, capitulo: value.capitulo, codigo: value.grupo.codigo, nombre: value.grupo.nombre,
                procesoConstructivo: value.tipo === 'proceso' ? value : undefined,
              });
            }}
            renderInput={(params) => <TextField {...params} variant="standard"
              inputProps={{ ...params.inputProps, 'aria-label': text('code') }}
              InputProps={{ ...params.InputProps, disableUnderline: true,
                endAdornment: <InputAdornment position="end"><>{searchLoading ? <CircularProgress size={16} aria-label={text('loading')} /> : <img className={searchStyles.searchIcon} src={searchIcon} alt="" />}</></InputAdornment> }}
              error={!!error?.some((item) => item.fieldField === 'codigo') || searchError} />} />
        </TitleValue>
      </div>
    </Grid>
    <Grid item xs={4}>
      <ConstructionSelect id={`data-process-chapter-${index}`} label={text('chapter')}
        value={selectedChapter} options={chapters} onChange={selectChapter} plain placeholder={text('chooseChapter')} />
    </Grid>
    <Grid item xs={3}>
      <ConstructionSelect id={`data-process-group-${index}`} label={text('group')}
        value={selectedGroup} options={groups} disabled={!chapterCode || catalogLoading || catalogError}
        onChange={selectGroup} placeholder={chapterCode ? text('chooseGroup') : text('chooseChapterFirst')}
        loading={catalogLoading} loadingLabel={text('loading')} error={!!error?.some((item) => item.fieldField === 'codigo')} />
    </Grid>
    <Grid item xs={3}>
      <ConstructionSelect id={`data-process-process-${index}`} label={text('process')}
        value={noProcesses ? undefined : selectedProcess} options={processes} disabled={!groupCode || catalogLoading || catalogError || noProcesses}
        onChange={selectProcess} placeholder={noProcesses ? '-' : groupCode ? text('chooseProcess') : text('chooseGroupFirst')}
        loading={loading} loadingLabel={text('loading')} notice={emptyUnits && !loading ? text('noUnits') : undefined} />
    </Grid>
    <Grid item xs={0.5}>
      <DeleteButton id={`deleteBtn-${index}`} onClick={() => {
        request.current += 1;
        handleDelete(process, index);
      }} />
    </Grid>
    {duplicateSelection && <Grid item xs={12}><Alert severity="error">{text('duplicateSelection')}</Alert></Grid>}
    {(failed || catalogError || searchError) && <Grid item xs={12}>
      <Alert severity="error" action={<Button onClick={() => {
        if (failed) void prepare(failed);
        else setReload((value) => value + 1);
      }}>{text('retry')}</Button>}>{text('loadError')}</Alert>
    </Grid>}
    <Dialog open={!!pending} onClose={() => { setPending(null); setInput(selectedCode); }}>
      <DialogTitle>{text('confirmTitle')}</DialogTitle>
      <DialogContent>{text('confirmReplace')}</DialogContent>
      <DialogActions>
        <Button onClick={() => { setPending(null); setInput(selectedCode); }}>{text('cancel')}</Button>
        <Button onClick={() => { if (pending) commit(pending); }}>{text('replace')}</Button>
      </DialogActions>
    </Dialog>
  </>;
};

export default DataProcess;
